import * as React from 'react'

// hero
import Hero from '../organisms/Hero'

import { graphql, PageProps } from 'gatsby'
import usePageData from '../../hooks/usePageData'
import SliceZone from '../SliceZone'
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews'
import SEO from '../Seo'
import ContactHero from '../organisms/ContactHero'
import { linkResolver } from '../../utils/linkResolver'

export type PageTemplateProps = PageProps<GatsbyTypes.PrismicPageQuery>

const PageTemplate: React.FC<PageTemplateProps> = ({ data }) => {
  const { hero, slices, seo } = usePageData(data)

  return (
    <>
      <SEO {...seo} />
      {hero &&
        (hero.form ? (
          <ContactHero data={hero} />
        ) : (
          <Hero
            smallGradient={hero.withSmallGradientBg}
            data={hero}
            paddingBottom={['52px', '', '170px']}
            paddingTop={['48px', '', '95px']}
          />
        ))}

      <SliceZone slices={slices} />
    </>
  )
}

export const query = graphql`
  query PrismicPage($id: String) {
    prismicPage(id: { eq: $id }) {
      _previewable
      id
      type
      uid
      lang
      data {
        ...PrismicPageSEO
        ...PrismicPageHero
        body {
          ...PrismicPageBodyNumberedList
          ...PrismicPageBodyTabs
          ...PrismicPageBodyLogosBanner
          ...PrismicPageBodyCallToAction
          ...PrismicPageBodyReasonsWhy
          ...PrismicPageBodyHeroBanner
          ...PrismicPageBodyDownloadPlatforms
          ...PrismicPageBodyComponentSection
          ...PrismicPageBodySpacer
          ...PrismicPageBodyIconsGrid
          ...PrismicPageBodyContact
          ...PrismicPageBodyProductTabs
          ...PrismicPageBodyFormulaTabs
          ...PrismicPageBodyText
          ...PrismicPageBodyTableTabs
          ...PrismicPageBodyPricingTabs
          ...PrismicPageBodyDocsTabs
          ...PrismicPageBodyCareers
          ...PrismicPageBodyPromotions
          ...PrismicPageBodyBrandAssets
          ...PrismicPageBodyFollowUs
          ...PrismicPageBodyBlogCategory
          ...PrismicPageBodyIframe
          ...PrismicPageBodyWebinars
          ...PrismicPageBodyPresenter
          ...PrismicPageBodyScript
          ...PrismicPageBodyPillars
          ...PrismicPageBodyTradingEnv
          ...PrismicPageBodyOurPhilosophy
          ...PrismicPageBodyLeverageTabs
        }
      }
    }
  }

  fragment PrismicPageSEO on PrismicPageDataType {
    seo_title
    seo_description
    seo_image {
      url
    }
    seo_twitter_card
    seo_twitter_title
    seo_twitter_description
    seo_twitter_site
    seo_twitter_image {
      url
    }
    seo_twitter_creator
    seo_sm_context
    seo_sm_type
    seo_sm_url
    seo_sm_name
    seo_sm_alternate_name
    seo_sm_description
    seo_sm_logo {
      url
    }
    seo_sm_same_as {
      seo_sm_same_as_item
    }
    seo_sm_contact_points {
      seo_sm_type
      seo_sm_contact_type
      seo_sm_name
      seo_sm_description
      seo_sm_telephone
      seo_sm_email
      seo_sm_hours_available
    }
  }

  fragment PrismicPageHero on PrismicPageDataType {
    is_hero_enabled
    is_hero_gradient
    title {
      text
    }
    subtitle {
      text
    }
    video {
      url
    }
    description {
      html
    }
    image {
      url
      alt
      dimensions {
        height
        width
      }
      gatsbyImageData(width: 604, layout: FIXED)
    }
    icon {
      alt
      url
      dimensions {
        height
        width
      }
    }
    button_link {
      id
      lang
      link_type
      type
      uid
      url
    }
    button_text
    button_type
    button_is_centered
    button_caption
    button_caption_mobile_size
    button_caption_desktop_size
    hero_contact_form {
      document {
        ...PrismicContactForm
      }
    }
  }
`

export default withPrismicPreview(PageTemplate, [
  {
    repositoryName: process.env.GATSBY_PRISMIC_REPOSITORY_NAME!,
    linkResolver
  }
])
